"use client"

import { TbFileText, TbCheckbox, TbChevronDown } from "react-icons/tb"
import { CATEGORIES } from "./data"

export type ItemizedBreakdownProps = {
   workingCosts: Record<string, number>
   openAccordions: Record<string, boolean>
   onToggleAccordion: (catKey: string) => void
   onItemCostChange: (id: string, val: string) => void
}

const DontForgetItems = [
   "Utility and location infrastructure security deposits",
   "Pre-opening staff training and onboarding",
   "Soft-opening launch and operational testing costs",
   "Permit validation buffer and local market extensions",
]

export default function ItemizedBreakdown({
   workingCosts,
   openAccordions,
   onToggleAccordion,
   onItemCostChange,
}: ItemizedBreakdownProps) {
   return (
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-9">
         <div className="mb-2 flex items-center gap-3 text-custom-2xl-bold">
            <TbFileText className="h-6 w-6 text-custom-primary shrink-0" />
            <span>Itemized Cost Breakdown Worksheet</span>
         </div>
         <p className="mb-6 text-custom-sm-medium">Click categories to expand. Adjust individual values manually.</p>

         {Object.keys(CATEGORIES).map((catKey) => {
            const category = CATEGORIES[catKey]
            const isOpen = !!openAccordions[catKey]
            const catSum = category.items.reduce((sum, item) => sum + (workingCosts[item.id] || 0), 0)

            return (
               <div key={catKey} className="mb-3 overflow-hidden rounded-md border border-slate-200">
                  <button
                     type="button"
                     onClick={() => onToggleAccordion(catKey)}
                     className="flex w-full select-none items-center justify-between bg-slate-50 p-3.5 px-4 text-custom-base-medium  transition-colors hover:bg-slate-100"
                  >
                     <div className="flex w-full items-center justify-between gap-2 pr-3">
                        <span className="min-w-0 text-left">{category.label}</span>
                        <span className="shrink-0 text-[#f15a29]">${catSum.toLocaleString()}</span>
                     </div>
                     <TbChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                     <div className="border-t border-slate-200 bg-white px-4 py-2">
                        {category.items.map((item) => (
                           <div
                              key={item.id}
                              className="flex flex-col gap-2 border-b border-slate-200 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                           >
                              <div className="flex-1 sm:pr-4">
                                 <div className="text-custom-sm-medium">{item.name}</div>
                                 <div className="mt-0.5 text-custom-14-medium">{item.desc}</div>
                              </div>
                              <div className="relative w-full sm:w-[120px]">
                                 <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-custom-sm-medium">
                                    $
                                 </span>
                                 <input
                                    type="number"
                                    value={workingCosts[item.id] ?? ""}
                                    onChange={(e) => onItemCostChange(item.id, e.target.value)}
                                    className="w-full rounded border border-slate-200 px-2 py-1.5 text-center text-custom-sm-medium focus:outline-none"
                                 />
                              </div>
                           </div>
                        ))}
                     </div>
                  )}
               </div>
            )
         })}

         <div className="mb-2 mt-6 text-custom-xl-bold">Don&apos;t Forget</div>
         <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4 sm:p-6">
            {DontForgetItems.map((item, index) => (
               <div key={index} className="flex items-start gap-3 text-custom-sm-medium">
                  <TbCheckbox className="mt-0.5 h-5 w-5 shrink-0 " />
                  <span>{item}</span>
               </div>
            ))}
         </div>
      </div>
   )
}
